import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNoteAlert } from "../../../Context/pleaseNote";
import { cardValidationError, TILL_ALLOWED_ORIGINS, TILL_FRAME_LOAD_TIMEOUT } from "../../../constants";
import { PAGES } from "../../../routeConfig";
import { useGlobalFunction } from "../../../Context/GlobalFunctionContext";
import { axiosInstance } from "../../../axios/axiosInstance";
import { useAuth } from "../../../Context/AuthContext";
import { Spinner } from "../../../Layout/Common/spinner";

// Name is shared with the pre-opened window in myWalletNewCard so that the
// second window.open() re-uses that tab instead of being blocked as a popup.
export const TILL_WALLET_WINDOW_NAME = "TillWalletWindow";

export const SaveCardPaymentGatewayWindow = ({ url, isDefault, callback }) => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const { loadStoredCard } = useGlobalFunction();
  const { showNoteAlert, showModelAlert, showFlashScreen } = useNoteAlert();

  const popupRef = useRef(null);
  const pollTimerRef = useRef(null);
  const loadTimerRef = useRef(null);
  const handledRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [frameTimedOut, setFrameTimedOut] = useState(false);
  const [newWindowMode, setNewWindowMode] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const cleanup = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    setShowModal(false);
    setFrameLoaded(false);
    setFrameTimedOut(false);
    setNewWindowMode(false);
    setPopupBlocked(false);
    setConfirming(false);
  };

  const paymentDeclineAlert = () => {
    if (handledRef.current) return;
    handledRef.current = true;
    cleanup();
    showFlashScreen(false);
    showModelAlert({
      title: cardValidationError["decline"].title,
      content: cardValidationError["decline"].content,
      icon: "wes-invalid-card",
    });
    if (callback) callback();
  };

  const handleSuccess = async (payload) => {
    if (handledRef.current) return;
    handledRef.current = true;

    try {
      var data = {
        transactionId: payload.transid,
        paystatus: payload.action,
        userId: String(userId),
        savecard: false,
        is_default: payload.isDefault ?? String(isDefault),
      };
      await axiosInstance.post("mywallet/success", data);
      cleanup();
      showFlashScreen(false);
      showNoteAlert("Your card has been saved successfully", "Saved Successfully", true, () => {
        loadStoredCard();
        navigate(PAGES.MYWALLET, { replace: true });
      });
    } catch (err) {
      console.error("Save card success handling failed", err);
      handledRef.current = false;
      paymentDeclineAlert();
    }
  };

  const handleResult = ({ status, transId, isDefault: defaultFlag }) => {
    if (handledRef.current) return;
    setConfirming(true);
    if (status === "success" || status === "successSave") {
      handleSuccess({ transid: transId, action: status, isDefault: defaultFlag });
    } else {
      paymentDeclineAlert();
    }
  };

  // Listen for postMessage from the gateway return page + localStorage fallback
  // (mobile / same-tab return, where there is no opener or parent to message).
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin && !TILL_ALLOWED_ORIGINS.includes(event.origin)) return;

      const data = event.data || {};
      if (data.source !== "tillWallet") return;
      if (!data.status || !data.transId) return;
      handleResult(data);
    };

    window.addEventListener("message", handleMessage);

    const stored = localStorage.getItem("tillWalletResult");
    if (stored) {
      localStorage.removeItem("tillWalletResult");
      handleResult(JSON.parse(stored));
    }

    return () => {
      window.removeEventListener("message", handleMessage);
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (!url) return;
    handledRef.current = false;
    // Where the same-tab fallback should land once the card is saved.
    localStorage.setItem("tillReturnUrl", PAGES.MYWALLETADD);
    setFrameLoaded(false);
    setFrameTimedOut(false);
    setNewWindowMode(false);
    setPopupBlocked(false);
    setShowModal(true);
  }, [url]);

  useEffect(() => {
    if (!showModal) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [showModal]);

  useEffect(() => {
    if (!showModal || newWindowMode || frameLoaded) return;
    loadTimerRef.current = setTimeout(() => setFrameTimedOut(true), TILL_FRAME_LOAD_TIMEOUT);
    return () => {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    };
  }, [showModal, newWindowMode, frameLoaded]);

  const openInNewWindow = () => {
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const features = `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`;

    const popup = window.open(url, TILL_WALLET_WINDOW_NAME, features);
    setNewWindowMode(true);

    if (!popup || popup.closed) {
      setPopupBlocked(true);
      return;
    }

    popupRef.current = popup;
    setPopupBlocked(false);
    pollTimerRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        paymentDeclineAlert();
      }
    }, 1000);
  };

  const handleOverlayClick = () => {
    if (popupBlocked || !popupRef.current || popupRef.current.closed) return;
    try {
      // focus() alone does not restore a minimised window in most browsers.
      // Re-targeting the window by name surfaces it; the empty URL means the
      // gateway page is not reloaded. Guarded above so this can never spawn
      // a stray blank popup.
      const existing = window.open("", TILL_WALLET_WINDOW_NAME);
      if (existing) {
        popupRef.current = existing;
        existing.focus();
      } else {
        popupRef.current.focus();
      }
    } catch (e) {
      popupRef.current.focus();
    }
  };

  const securedByTill = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#2e7d32" }}>
      <i className="fa-solid fa-lock" aria-hidden="true" />
      Secured by Till
    </span>
  );

  if (confirming) {
    return createPortal(
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          zIndex: 100000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: "8px",
            padding: "28px 32px",
            textAlign: "center",
            maxWidth: "420px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <Spinner size={48} />
          <p style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Saving your card...</p>
          <p style={{ margin: 0, fontSize: "13px", color: "#666" }}>Please do not refresh or close this page.</p>
          {securedByTill}
        </div>
      </div>,
      document.body,
    );
  }

  if (!showModal || !url) return null;

  if (newWindowMode) {
    return createPortal(
      <div
        onClick={handleOverlayClick}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          // Above .paymentFlashScreen (also 9999), which renders after children
          // in the DOM and would otherwise win the tie and eat backdrop clicks.
          zIndex: 100000,
          cursor: popupBlocked ? "default" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "12px",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: "8px",
            padding: "24px 32px",
            textAlign: "center",
            maxWidth: "420px",
          }}
        >
          {popupBlocked ? (
            <>
              <p style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Popup Blocked</p>
              <p style={{ margin: "12px 0 16px", fontSize: "14px", color: "#555" }}>
                Your browser blocked the card verification window. Please click below to allow and proceed.
              </p>
              <button
                type="button"
                onClick={openInNewWindow}
                style={{
                  background: "#00718b",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 28px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Allow &amp; Open Verification Window
              </button>
            </>
          ) : (
            <>
              <p style={{ margin: 0, fontSize: "16px", fontWeight: 500 }}>Card verification in progress...</p>
              <p style={{ margin: "8px 0 12px", fontSize: "13px", color: "#666" }}>
                Please complete the verification in the new window.
              </p>
              {securedByTill}
            </>
          )}
          <div style={{ marginTop: "16px" }}>
            <button
              type="button"
              onClick={paymentDeclineAlert}
              style={{
                background: "transparent",
                border: "none",
                color: "#00718b",
                fontSize: "13px",
                textDecoration: "underline",
                cursor: "pointer",
                padding: 0,
              }}
            >
              Cancel verification
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        // Above .paymentFlashScreen (also 9999), which renders after children
        // in the DOM and would otherwise win the tie.
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "12px",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "8px",
          width: "100%",
          maxWidth: "620px",
          height: "min(92vh, 760px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            padding: "8px 12px",
            borderBottom: "1px solid #e5e5e5",
            flex: "0 0 auto",
          }}
        >
          <button
            type="button"
            onClick={paymentDeclineAlert}
            aria-label="Cancel verification"
            style={{
              background: "transparent",
              border: "none",
              fontSize: "26px",
              lineHeight: 1,
              cursor: "pointer",
              color: "#555",
              padding: "0 4px",
              display: "inline-flex",
            }}
          >
            <i className="fa-regular fa-circle-xmark" aria-hidden="true" />
          </button>
        </div>

        <div style={{ position: "relative", flex: "1 1 auto" }}>
          {!frameLoaded && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "12px",
                padding: "24px",
                textAlign: "center",
                fontSize: "14px",
                color: "#666",
                background: "#fff",
              }}
            >
              {frameTimedOut ? (
                <>
                  <p style={{ margin: 0, fontWeight: 600, color: "#333" }}>
                    The verification page is taking longer than expected.
                  </p>
                  <p style={{ margin: 0 }}>Open it in a new window to continue saving your card.</p>
                  <button
                    type="button"
                    onClick={openInNewWindow}
                    style={{
                      background: "#00718b",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "10px 28px",
                      fontSize: "15px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Open in a New Window
                  </button>
                </>
              ) : (
                <>
                  <Spinner size={48} />
                  <span>Loading secure verification page...</span>
                </>
              )}
            </div>
          )}
          <iframe
            title="Till Card Verification"
            src={url}
            onLoad={() => setFrameLoaded(true)}
            allow="payment *; local-network-access *"
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        </div>

        <div
          style={{
            padding: "10px 16px",
            borderTop: "1px solid #e5e5e5",
            fontSize: "12px",
            color: "#666",
            textAlign: "center",
            flex: "0 0 auto",
          }}
        >
          <div>Please do not refresh or close this window while the verification is in progress.</div>
          <button
            type="button"
            onClick={openInNewWindow}
            style={{
              background: "transparent",
              border: "none",
              color: "#00718b",
              fontSize: "12px",
              textDecoration: "underline",
              cursor: "pointer",
              padding: "4px 0 0",
            }}
          >
            Trouble loading? Open in a new window
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
